import { useState } from "react"
import styled from "styled-components"

import { Banner, Checkbox, Text, TextField } from "@minuthq/meatball-ui-react"

import { usePutParadiseUser } from "src/data/paradise/paradiseUsers/queries/paradiseUserQueries"
import { TParadiseUser } from "src/data/paradise/paradiseUsers/types/paradiseUserQueryTypes"
import { MDialog } from "src/ui/Dialog/MDialog"
import { MTooltip } from "src/ui/MTooltip/MTooltip"
import { spacing } from "src/ui/spacing"

export function ParadiseChangeUserEmailDialog({
  open,
  user,
  onClose,
}: {
  open: boolean
  user: TParadiseUser
  onClose: () => void
}) {
  const [newEmail, setNewEmail] = useState(user.email)
  const putParadiseUser = usePutParadiseUser()
  const [checked, setChecked] = useState(false)

  function close() {
    setNewEmail(user.email)
    setChecked(false)
    onClose()
  }

  function changeEmail() {
    putParadiseUser.mutate(
      {
        path: {
          user_id: user.id,
        },
        body: {
          email: newEmail,
        },
      },
      {
        onSuccess: close,
      }
    )
  }

  return (
    <MDialog
      title="Change email?"
      open={open}
      onClose={close}
      confirmLabel="Change email"
      onConfirm={changeEmail}
      width="large"
      error={putParadiseUser.error?.message}
      loading={putParadiseUser.isLoading}
      confirmButtonProps={{
        disabled: !checked || user.email === newEmail,
      }}
    >
      <Wrapper>
        <Text variant="body">
          Changing the email will send two emails:
          <ul>
            <li>A verification email to confirm the new email address</li>
            <li>
              An information email to the old email address that the address has
              been changed
            </li>
          </ul>
        </Text>
        <Banner type="warning">
          Ensure you've verified the customer owns the account, for example by
          asking them to provide invoices. (unless it's an obvious typo)
        </Banner>
        <TextField
          placeholder={user.email}
          value={newEmail}
          onChange={(value) => setNewEmail(value)}
        />
        <MTooltip
          title="The new email address must be different from the old email address"
          enabled={user.email === newEmail}
        >
          <Checkbox
            label="I've verified the customer owns the account"
            checked={checked}
            disabled={user.email === newEmail}
            onCheck={(value) => setChecked(value)}
          />
        </MTooltip>
      </Wrapper>
    </MDialog>
  )
}

const Wrapper = styled.div`
  display: grid;
  gap: ${spacing.M};
`
